import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {
  createSecureSubmissionPreviewSession,
  loadSecureSubmissionDocumentByPreviewSession,
  loadSecureSubmissionView,
  refreshSecureSubmissionPreviewSession,
  SecureSubmissionViewResult,
} from '../../api'
import SecureDocumentViewerModal from '../shared/secure-document-viewer/SecureDocumentViewerModal'
import {useSecureDocumentViewer} from '../shared/secure-document-viewer/useSecureDocumentViewer'
import PmAccessGate from '../pm-access/PmAccessGate'
import {usePmProtectedRequest} from '../pm-access/PmAccessContext'
import '../../style/application.scss'

type DraftEmploymentShape = {
  employmentId?: number
  companyName?: string
  employmentType?: string
  startDate?: string
  isStartMonthUnknown?: boolean
  employerName?: string
  employerPhone?: string
  employerEmail?: string
  employerReference?: string
  abn?: string
  additionalReferenceName?: string
  additionalReferencePhone?: string
  additionalReferenceEmail?: string
  hasEmploymentIncome?: boolean
  hasSelfEmploymentIncome?: boolean
  income?: number
  incomeFrequency?: string
  selfEmploymentIncome?: number
  selfEmploymentIncomeFrequency?: string
  isCentreLink?: boolean
  centreAmount?: number
  centreLinkFrequency?: string
  hasPension?: boolean
  pensionAmount?: number
  pensionFrequency?: string
  hasNdis?: boolean
  ndisAmount?: number
  ndisFrequency?: string
  hasStudentSupport?: boolean
  studentSupportAmount?: number
  studentSupportFrequency?: string
}

type DraftIncomeShape = {
  hasEmploymentIncome?: boolean
  hasSelfEmploymentIncome?: boolean
  income?: number
  incomeFrequency?: string
  selfEmploymentIncome?: number
  selfEmploymentIncomeFrequency?: string
  isCentreLink?: boolean
  centreAmount?: number
  centreLinkFrequency?: string
  hasPension?: boolean
  pensionAmount?: number
  pensionFrequency?: string
  hasNdis?: boolean
  ndisAmount?: number
  ndisFrequency?: string
  hasStudentSupport?: boolean
  studentSupportAmount?: number
  studentSupportFrequency?: string
}

type DraftPetShape = {
  type?: string
  breed?: string
  isAssistanceAnimal?: boolean
}

type DraftVehicleShape = {
  makeModel?: string
  registrationNumber?: string
}

type DraftShape = {
  applicationId?: number
  sessionId?: string
  stepKey?: string
  blockNotVerifiedCoApplicant?: boolean
  status?: string
  renterRole?: string
  firstName?: string
  lastName?: string
  mobileNumber?: string
  email?: string
  dateOfBirth?: string
  termsAccepted?: boolean
  consentSharingAccepted?: boolean
  isIdentityVerified?: boolean
  isMobileVerified?: boolean
  mobileVerifiedAtUtc?: string
  lastSavedAtUtc?: string
  applicationType?: string
  companyTrustAbnAcn?: string
  companyTrustDirectorId?: string
  companyTrustLetterOfAuthorityDocumentId?: string
  emergencyContactName?: string
  emergencyContactPhone?: string
  emergencyContactRelationship?: string
  moveInAsap?: boolean
  moveInPreferredDate?: string
  preferredLeaseTerm?: string
  rentWeeklyBudgetMin?: number
  rentWeeklyBudgetMax?: number
  previousRentalHistory?: boolean
  previousPropertyManager?: string
  previousPropertyManagerPhone?: string
  previousRentWeekly?: number
  previousRentDuration?: string
  rentalHistoryReasonForLeaving?: string
  bondFullyRefundedAtLastAddress?: boolean
  identificationType?: string
  isVisaHolder?: boolean
  isInternationalStudent?: boolean
  retentionPreference?: number
  auditTransparencyAcknowledged?: boolean
  coApplicants?: Array<{
    inviteId?: string
    fullName?: string
    mobileNumber?: string
    email?: string
    role?: string
    status?: string
    isMobileVerified?: boolean
    invitedAtUtc?: string
    inviteExpiresAtUtc?: string
    verifiedAtUtc?: string
    revokedAtUtc?: string
  }>
  employment?: DraftEmploymentShape
  income?: DraftIncomeShape
  employments?: DraftEmploymentShape[]
  pets?: DraftPetShape[]
  vehicles?: DraftVehicleShape[]
  documents?: DraftDocumentShape[]
}

type DraftDocumentShape = {
  documentId?: string
  type?: string
  filename?: string
  contentType?: string
  uploadedAtUtc?: string
  verificationStatus?: string
}

type SnapshotShape = {
  submissionId?: string
  submittedAtUtc?: string
  applicationId?: number
  sessionId?: string
  draft?: DraftShape
  coApplicantDrafts?: CoApplicantSnapshotShape[]
}

type CoApplicantSnapshotShape = {
  inviteId?: string
  coApplicantApplicationId?: number
  fullName?: string
  mobileNumber?: string
  email?: string
  role?: string
  status?: string
  isMobileVerified?: boolean
  invitedAtUtc?: string
  inviteExpiresAtUtc?: string
  verifiedAtUtc?: string
  revokedAtUtc?: string
  draft?: DraftShape
}

const formatDateTime = (value?: string): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-AU')
}

const formatDate = (value?: string): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-AU')
}

const formatEmploymentStartDate = (employment: DraftEmploymentShape): string => {
  if (!employment.startDate) return '-'
  if (!employment.isStartMonthUnknown) return formatDate(employment.startDate)

  const year = new Date(employment.startDate).getUTCFullYear()
  return Number.isNaN(year) ? employment.startDate : `${year} (month unknown)`
}

const toCurrency = (value?: number): string => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return '-'
  }

  return Number(value).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  })
}

const formatBoolean = (value?: boolean): string => {
  if (value === undefined || value === null) {
    return '-'
  }

  return value ? 'Yes' : 'No'
}

const formatText = (value?: string | number | null): string => {
  if (value === undefined || value === null) {
    return '-'
  }

  const normalized = String(value).trim()
  return normalized ? normalized : '-'
}

const getDocumentTypeName = (value: string, prefix: string = ''): string => {
  switch (value) {
    case 'identity-front':
      return prefix + ' Front'
    case 'identity-back':
      return prefix + ' Back'
    case 'rental-ledger':
      return 'Rental Ledger'
    case 'income-doc-1':
      return 'Income Document 1'
    case 'income-doc-2':
      return 'Income Document 2'
    case 'income-doc-3':
      return 'Income Document 3'
    case 'visa-grant-notice':
      return 'Visa Grant Notice'
    case 'international-coe':
      return 'Certificate of Enrolment'
    case 'enrollment-letter':
      return 'Enrolment Letter'

    default:
      return value
  }
}

const formatMoveIn = (draft: DraftShape): string => {
  if (draft.moveInAsap === true) {
    return 'ASAP'
  }

  if (draft.moveInPreferredDate) {
    return formatDate(draft.moveInPreferredDate)
  }

  return '-'
}

const formatName = (draft: DraftShape): string => {
  const name = `${draft.firstName ?? ''} ${draft.lastName ?? ''}`.trim()
  return name || '-'
}

const isRevokedCoApplicant = (status?: string, revokedAtUtc?: string): boolean => {
  if (revokedAtUtc && revokedAtUtc.trim()) {
    return true
  }

  return (status ?? '').trim().toLowerCase() === 'revoked'
}

type DetailFieldProps = {
  label: string
  value: React.ReactNode
  className?: string
}

const DetailField: React.FC<DetailFieldProps> = ({label, value, className}) => (
  <div className={className ?? 'col-12 col-md-6 col-xl-4'}>
    <div className='secure-field-label'>{label}</div>
    <div className='secure-field-value'>{value}</div>
  </div>
)

type DetailGroupProps = {
  title: string
  hint?: string
  defaultOpen?: boolean
  children: React.ReactNode
}

const DetailGroup: React.FC<DetailGroupProps> = ({title, hint, defaultOpen, children}) => (
  <details className='secure-detail-group' open={defaultOpen ?? true}>
    <summary className='secure-detail-group-summary'>
      <span>{title}</span>
      {hint ? <span className='secure-detail-group-hint'>{hint}</span> : null}
    </summary>
    <div className='secure-detail-group-body'>{children}</div>
  </details>
)

type PersonDetailsSectionProps = {
  heading: string
  draft?: DraftShape
  invite?: CoApplicantSnapshotShape
  onOpenDocument: (document: DraftDocumentShape) => void
}

const PersonDetailsSection: React.FC<PersonDetailsSectionProps> = ({
  heading,
  draft,
  invite,
  onOpenDocument,
}) => {
  const employment = draft?.employment ?? draft?.employments?.[0]
  const income = draft?.income ?? draft?.employments?.[0]
  const employmentCount = employment ? 1 : 0
  const petCount = draft?.pets?.length ?? 0
  const vehicleCount = draft?.vehicles?.length ?? 0
  const documentCount = draft?.documents?.length ?? 0

  if (!draft) {
    return (
      <section className='secure-person-card'>
        <div className='secure-person-header'>
          <div>
            <h3 className='h6 mb-1'>{heading}</h3>
            <div className='small text-muted'>{formatText(invite?.fullName)}</div>
          </div>
          <div className='secure-pill-row'>
            <span className='secure-pill'>Invite: {formatText(invite?.status)}</span>
          </div>
        </div>

        <DetailGroup title='Invite Details'>
          <div className='row g-3'>
            <DetailField label='Invite ID' value={formatText(invite?.inviteId)} />
            <DetailField
              label='Linked Co-app ID'
              value={formatText(invite?.coApplicantApplicationId)}
            />
            <DetailField label='Role' value={formatText(invite?.role)} />
            <DetailField label='Mobile' value={formatText(invite?.mobileNumber)} />
            <DetailField label='Email' value={formatText(invite?.email)} />
            <DetailField label='Mobile Verified' value={formatBoolean(invite?.isMobileVerified)} />
            <DetailField label='Invited At' value={formatDateTime(invite?.invitedAtUtc)} />
            <DetailField
              label='Invite Expires'
              value={formatDateTime(invite?.inviteExpiresAtUtc)}
            />
            <DetailField label='Verified At' value={formatDateTime(invite?.verifiedAtUtc)} />
            <DetailField label='Revoked At' value={formatDateTime(invite?.revokedAtUtc)} />
          </div>
        </DetailGroup>

        <div className='alert alert-light mt-3 mb-0'>
          Full co-applicant application details are not yet linked for this invite.
        </div>
      </section>
    )
  }

  return (
    <section className='secure-person-card'>
      <div className='secure-person-header'>
        <div>
          <h3 className='h6 mb-1'>{heading}</h3>
          <div className='small text-muted'>{formatName(draft)}</div>
        </div>
        <div className='secure-pill-row'>
          <span className='secure-pill d-none'>Status: {formatText(draft.status)}</span>
          <span className='secure-pill d-none'>Role: {formatText(draft.renterRole)}</span>
          <span className='secure-pill d-none'>
            Verified: {formatBoolean(draft.isMobileVerified)}
          </span>
          <span className='secure-pill'>Employments: {employmentCount}</span>
          <span className='secure-pill'>Documents: {documentCount}</span>
        </div>
      </div>

      <DetailGroup title='Identity & Contact'>
        <div className='row g-3'>
          <DetailField label='Full Name' value={formatName(draft)} />
          <DetailField label='Date Of Birth' value={formatDate(draft.dateOfBirth)} />
          <DetailField label='Mobile Number' value={formatText(draft.mobileNumber)} />
          <DetailField label='Email' value={formatText(draft.email)} />
        </div>
      </DetailGroup>

      <DetailGroup title='Application Setup'>
        <div className='row g-3'>
          <DetailField label='Application Type' value={formatText(draft.applicationType)} />
          {draft.applicationType === 'CompanyTrust' && (
            <>
              <DetailField
                label='Company/Trust ABN/ACN'
                value={formatText(draft.companyTrustAbnAcn)}
              />
              <DetailField
                label='Company/Trust Director ID'
                value={formatText(draft.companyTrustDirectorId)}
              />
              <DetailField
                label='Letter Of Authority Document ID'
                value={formatText(draft.companyTrustLetterOfAuthorityDocumentId)}
              />
            </>
          )}
        </div>
      </DetailGroup>

      <DetailGroup title='Move-in Preferences'>
        <div className='row g-3'>
          <DetailField label='Move-in Preference' value={formatMoveIn(draft)} />
          <DetailField label='Preferred Lease Term' value={formatText(draft.preferredLeaseTerm)} />
          <DetailField
            label='Rent Weekly Budget Min'
            value={toCurrency(draft.rentWeeklyBudgetMin)}
          />
          <DetailField
            label='Rent Weekly Budget Max'
            value={toCurrency(draft.rentWeeklyBudgetMax)}
          />
        </div>
      </DetailGroup>

      <DetailGroup title='Emergency & Identification' defaultOpen={false}>
        <div className='row g-3'>
          <DetailField
            label='Emergency Contact Name'
            value={formatText(draft.emergencyContactName)}
          />
          <DetailField
            label='Emergency Contact Phone'
            value={formatText(draft.emergencyContactPhone)}
          />
          <DetailField
            label='Emergency Contact Relationship'
            value={formatText(draft.emergencyContactRelationship)}
          />
          <DetailField
            label='Identification Type'
            value={formatText(draft.identificationType)}
          />
          <DetailField label='Visa Holder' value={formatBoolean(draft.isVisaHolder)} />
          <DetailField
            label='International Student'
            value={formatBoolean(draft.isInternationalStudent)}
          />
        </div>
      </DetailGroup>

      <DetailGroup title='Rental History' defaultOpen={false}>
        <div className='row g-3'>
          <DetailField
            label='Previous Rental History'
            value={formatBoolean(draft.previousRentalHistory)}
          />
          {draft.previousRentalHistory && (
            <>
              <DetailField
                label='Previous Property Manager'
                value={formatText(draft.previousPropertyManager)}
              />
              <DetailField
                label='Previous Property Manager Phone'
                value={formatText(draft.previousPropertyManagerPhone)}
              />
              <DetailField
                label='Previous Rent Weekly'
                value={toCurrency(draft.previousRentWeekly)}
              />
              <DetailField
                label='Previous Rent Duration'
                value={formatText(draft.previousRentDuration)}
              />
              <DetailField
                label='Reason For Leaving'
                value={formatText(draft.rentalHistoryReasonForLeaving)}
              />
              <DetailField
                label='Bond Fully Refunded'
                value={formatBoolean(draft.bondFullyRefundedAtLastAddress)}
              />
            </>
          )}
        </div>
      </DetailGroup>

      <DetailGroup title='Employment' hint={`${employmentCount} record(s)`}>
        {employment ? (
          <div className='d-flex flex-column gap-2'>
            <details
              key={`${employment.employmentId ?? 'employment'}-0`}
              className='secure-subgroup'
              open
            >
              <summary className='secure-subgroup-summary'>
                <span>
                  {formatText(employment.employmentType)}
                  {employment.companyName ? ` - ${employment.companyName}` : ''}
                </span>
                <span className='secure-subgroup-meta'>
                  {formatEmploymentStartDate(employment)}
                </span>
              </summary>
              <div className='secure-subgroup-body'>
                <div className='row g-3'>
                  <DetailField label='Type' value={formatText(employment.employmentType)} />
                  {employment.employmentType !== 'self-employed' &&
                    employment.employmentType !== 'unemployed' && (
                      <>
                        <DetailField label='Company' value={formatText(employment.companyName)} />
                        <DetailField
                          label='Start Date'
                          value={formatEmploymentStartDate(employment)}
                        />
                        <DetailField
                          label='Employer Name'
                          value={formatText(employment.employerName)}
                        />
                        <DetailField
                          label='Employer Phone'
                          value={formatText(employment.employerPhone)}
                        />
                        <DetailField
                          label='Employer Email'
                          value={formatText(employment.employerEmail)}
                        />
                        <DetailField
                          label='Employer Reference'
                          value={formatText(employment.employerReference)}
                        />
                      </>
                    )}
                  {employment.employmentType === 'self-employed' && (
                    <>
                      <DetailField label='ABN' value={formatText(employment.abn)} />
                    </>
                  )}

                  <hr />
                  <DetailField label='Income' value={toCurrency(income?.income)} />
                  <DetailField
                    label='Income Frequency'
                    value={formatText(income?.incomeFrequency)}
                  />
                  <DetailField label='Centrelink' value={formatBoolean(income?.isCentreLink)} />
                  {income?.isCentreLink && (
                    <>
                      <DetailField
                        label='Centrelink Amount'
                        value={toCurrency(income.centreAmount)}
                      />
                      <DetailField
                        label='Centrelink Frequency'
                        value={formatText(income.centreLinkFrequency)}
                      />
                    </>
                  )}
                  <DetailField label='Has Pension' value={formatBoolean(income?.hasPension)} />
                  {income?.hasPension && (
                    <>
                      <DetailField
                        label='Pension Amount'
                        value={toCurrency(income.pensionAmount)}
                      />
                      <DetailField
                        label='Pension Frequency'
                        value={formatText(income.pensionFrequency)}
                      />
                    </>
                  )}
                  <DetailField label='Has NDIS' value={formatBoolean(income?.hasNdis)} />
                  {income?.hasNdis && (
                    <>
                      <DetailField label='NDIS Amount' value={toCurrency(income.ndisAmount)} />
                      <DetailField
                        label='NDIS Frequency'
                        value={formatText(income.ndisFrequency)}
                      />
                    </>
                  )}
                  <DetailField
                    label='Has Student Support'
                    value={formatBoolean(income?.hasStudentSupport)}
                  />
                  {income?.hasStudentSupport && (
                    <>
                      <DetailField
                        label='Student Support Amount'
                        value={toCurrency(income.studentSupportAmount)}
                      />
                      <DetailField
                        label='Student Support Frequency'
                        value={formatText(income.studentSupportFrequency)}
                      />
                    </>
                  )}
                </div>
              </div>
            </details>
          </div>
        ) : (
          <div className='text-muted'>No employment records captured.</div>
        )}
      </DetailGroup>

      <DetailGroup
        title='Household & Assets'
        hint={`${petCount} pet(s), ${vehicleCount} vehicle(s)`}
        defaultOpen={false}
      >
        <div className='row g-3'>
          <div className='col-12 col-lg-6'>
            <div className='secure-asset-panel'>
              <div className='secure-asset-panel-header'>
                <h4 className='h6 mb-0'>Pets</h4>
                <span className='secure-asset-count'>{petCount}</span>
              </div>
              {petCount > 0 ? (
                <div className='secure-asset-list'>
                  {draft.pets!.map((pet, index) => (
                    <div key={`${pet.type ?? 'pet'}-${index}`} className='secure-asset-item'>
                      <div className='secure-asset-item-top'>
                        <span className='secure-asset-title'>{formatText(pet.type)}</span>
                      </div>
                      <div className='secure-asset-meta'>Breed: {formatText(pet.breed)}</div>
                      <div className='secure-asset-meta'>
                        Is Assistance Animal: {formatBoolean(pet.isAssistanceAnimal)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='text-muted'>No pets declared.</div>
              )}
            </div>
          </div>
          <div className='col-12 col-lg-6'>
            <div className='secure-asset-panel'>
              <div className='secure-asset-panel-header'>
                <h4 className='h6 mb-0'>Vehicles</h4>
                <span className='secure-asset-count'>{vehicleCount}</span>
              </div>
              {vehicleCount > 0 ? (
                <div className='secure-asset-list'>
                  {draft.vehicles!.map((vehicle, index) => (
                    <div
                      key={`${vehicle.makeModel ?? 'vehicle'}-${index}`}
                      className='secure-asset-item'
                    >
                      <div className='secure-asset-item-top'>
                        <span className='secure-asset-title'>{formatText(vehicle.makeModel)}</span>
                        <span className='secure-asset-badge'></span>
                      </div>
                      <div className='secure-asset-meta'>
                        Registration: {formatText(vehicle.registrationNumber)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='text-muted'>No vehicles declared.</div>
              )}
            </div>
          </div>
        </div>
      </DetailGroup>

      <DetailGroup title='Documents' hint={`${documentCount} file(s)`}>
        {documentCount > 0 ? (
          <div className='table-responsive'>
            <table className='table table-sm align-middle mb-0'>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Uploaded</th>
                  <th>Status</th>
                  <th className='text-end'></th>
                </tr>
              </thead>
              <tbody>
                {draft.documents!.map((document, index) => (
                  <tr key={`${document.documentId ?? document.filename ?? 'doc'}-${index}`}>
                    <td>
                      {getDocumentTypeName(
                        formatText(document.type),
                        draft.identificationType
                      )}
                    </td>
                    <td>{formatDateTime(document.uploadedAtUtc)}</td>
                    <td>{formatText(document.verificationStatus)}</td>
                    <td className='text-end'>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        disabled={!document.documentId}
                        onClick={() => onOpenDocument(document)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className='text-muted'>No documents captured.</div>
        )}
      </DetailGroup>
    </section>
  )
}

const SecureSubmissionViewPageContent: React.FC = () => {
  const protectedRequest = usePmProtectedRequest()
  const {token} = useParams<{token: string}>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [payload, setPayload] = useState<SecureSubmissionViewResult | null>(null)

  useEffect(() => {
    let isCancelled = false

    const run = async () => {
      if (!token) {
        setError('Submission token is missing.')
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const result = await protectedRequest(() => loadSecureSubmissionView(token))
        if (isCancelled) {
          return
        }

        setPayload(result)
      } catch (err) {
        if (isCancelled) {
          return
        }

        if (err instanceof Error) {
          setError(err.message)
        } else {
          setError('Unable to load this submission.')
        }
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }

    run()

    return () => {
      isCancelled = true
    }
  }, [token, protectedRequest])

  const acquirePreviewSession = useCallback(async () => {
    if (!token) {
      throw new Error('Submission token is missing.')
    }

    return protectedRequest(() => createSecureSubmissionPreviewSession(token))
  }, [token, protectedRequest])

  const refreshPreviewSession = useCallback(
    async (currentPreviewToken: string | null) => {
      if (currentPreviewToken) {
        return protectedRequest(() => refreshSecureSubmissionPreviewSession(currentPreviewToken))
      }

      if (!token) {
        throw new Error('Submission token is missing.')
      }

      return protectedRequest(() => createSecureSubmissionPreviewSession(token))
    },
    [token, protectedRequest]
  )

  const loadViewerDocument = useCallback(
    async (previewToken: string, document: DraftDocumentShape) => {
      if (!document.documentId) {
        throw new Error('Document cannot be viewed because its identifier is missing.')
      }

      return protectedRequest(() => loadSecureSubmissionDocumentByPreviewSession(previewToken, document.documentId!))
    },
    [protectedRequest]
  )

  const {
    viewerDocument,
    viewerDocumentKind,
    viewerSrc,
    viewerPdfPages,
    viewerLoading,
    viewerError,
    viewerStatus,
    zoomPercent,
    canZoomIn,
    canZoomOut,
    canGoToPreviousPage,
    canGoToNextPage,
    currentPageIndex,
    pageCount,
    openViewer,
    retryPreview,
    reloadPreview,
    zoomIn,
    zoomOut,
    fitPreview,
    resetPreview,
    goToPreviousPage,
    goToNextPage,
    closeViewer,
  } = useSecureDocumentViewer<DraftDocumentShape>({
    acquirePreviewSession,
    refreshPreviewSession,
    loadDocument: loadViewerDocument,
  })

  const snapshot = payload?.snapshot as SnapshotShape | undefined
  const draft = useMemo(() => snapshot?.draft ?? {}, [snapshot]) as DraftShape
  const coApplicantDrafts = useMemo(
    () => (snapshot?.coApplicantDrafts ?? []) as CoApplicantSnapshotShape[],
    [snapshot]
  )
  const coApplicantsToDisplay = useMemo(() => {
    if (coApplicantDrafts.length > 0) {
      return coApplicantDrafts.filter(
        (item) => !isRevokedCoApplicant(item.status, item.revokedAtUtc)
      )
    }

    const fallbackInvites = (draft.coApplicants ?? []).filter(
      (invite) => !isRevokedCoApplicant(invite.status, invite.revokedAtUtc)
    )
    return fallbackInvites.map((invite, index) => ({
      inviteId: invite.inviteId,
      coApplicantApplicationId: undefined,
      fullName: invite.fullName,
      mobileNumber: invite.mobileNumber,
      email: invite.email,
      role: invite.role,
      status: invite.status,
      isMobileVerified: invite.isMobileVerified,
      invitedAtUtc: invite.invitedAtUtc,
      inviteExpiresAtUtc: invite.inviteExpiresAtUtc,
      verifiedAtUtc: invite.verifiedAtUtc,
      revokedAtUtc: invite.revokedAtUtc,
      draft: undefined,
    }))
  }, [coApplicantDrafts, draft.coApplicants])
  if (loading) {
    return (
      <main className='container py-5 secure-submission-page'>
        <div className='card shadow-sm border-0'>
          <div className='card-body p-4 p-md-5'>Loading secure submission view...</div>
        </div>
      </main>
    )
  }

  if (error) {
    return (
      <main className='container py-5 secure-submission-page'>
        <div className='card shadow-sm border-0'>
          <div className='card-body p-4 p-md-5'>
            <h2 className='h4 mb-3'>Secure View Unavailable</h2>
            <div className='text-danger mb-3'>{error}</div>
            <Link to='/application-landing' className='btn btn-light-primary'>
              Back to Doora
            </Link>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className='container py-4 py-md-5 secure-submission-page'>
      <div className='d-flex flex-column gap-4'>
        <section className='card shadow-sm border-0'>
          <div className='card-body p-4 p-md-5'>
            <h1 className='h3 mb-2'>Doora Card Submission</h1>
            <div className='text-muted mb-1'>Web-only secure view</div>
            <div className='small text-muted'>
              Submitted: {formatDateTime(snapshot?.submittedAtUtc)} | Expires:{' '}
              {formatDateTime(payload?.expiresAtUtc)}
            </div>
            <div className='alert alert-warning py-2 px-3 mt-3 mb-0 d-none'>
              View-only mode: no download or print actions are provided in this interface.
            </div>
            <div className='small text-muted mt-2 d-none'>
              PDF files are rendered as image pages in secure mode to avoid native browser PDF
              toolbar actions.
            </div>
          </div>
        </section>

        <section className='card shadow-sm border-0'>
          <div className='card-body p-4 p-md-5 d-flex flex-column gap-4'>
            <h2 className='h5 mb-0'>Complete Application Details</h2>
            <PersonDetailsSection
              heading='Applicant'
              draft={draft}
              onOpenDocument={(document) => {
                void openViewer(document)
              }}
            />

            {coApplicantsToDisplay.length > 0 ? (
              coApplicantsToDisplay.map((coApplicant, index) => (
                <PersonDetailsSection
                  key={`${coApplicant.inviteId ?? 'coapp'}-${index}`}
                  heading={`Co-applicant ${index + 1}`}
                  draft={coApplicant.draft}
                  invite={coApplicant}
                  onOpenDocument={(document) => {
                    void openViewer(document)
                  }}
                />
              ))
            ) : (
              <section className='border rounded p-3 p-md-4'>
                <h3 className='h6 mb-0'>Co-applicants</h3>
                <div className='text-muted mt-2'>None</div>
              </section>
            )}
          </div>
        </section>
      </div>

      <SecureDocumentViewerModal
        document={viewerDocument}
        loading={viewerLoading}
        error={viewerError}
        status={viewerStatus}
        documentKind={viewerDocumentKind}
        src={viewerSrc}
        pdfPages={viewerPdfPages}
        currentPageIndex={currentPageIndex}
        pageCount={pageCount}
        zoomPercent={zoomPercent}
        canZoomIn={canZoomIn}
        canZoomOut={canZoomOut}
        canGoToPreviousPage={canGoToPreviousPage}
        canGoToNextPage={canGoToNextPage}
        onZoomIn={() => {
          void zoomIn()
        }}
        onZoomOut={() => {
          void zoomOut()
        }}
        onFit={(fitBounds) => {
          void fitPreview(fitBounds)
        }}
        onReset={() => {
          void resetPreview()
        }}
        onRetry={() => {
          void retryPreview()
        }}
        onReload={() => {
          void reloadPreview()
        }}
        onGoToPreviousPage={goToPreviousPage}
        onGoToNextPage={goToNextPage}
        onClose={closeViewer}
      />
    </main>
  )
}

const SecureSubmissionViewPage: React.FC = () => <PmAccessGate><SecureSubmissionViewPageContent /></PmAccessGate>

export default SecureSubmissionViewPage
